import React, { useMemo, useRef, useEffect } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { textTexture, crossTexture, dangerTexture, monitorTexture } from "./textures";
import { Figure } from "./Figure";

function usePhotoTexture(url) {
  return useMemo(() => {
    const t = new THREE.TextureLoader().load(url);
    t.colorSpace = THREE.SRGBColorSpace;
    return t;
  }, [url]);
}

export function GreenScreen() {
  const cross = useMemo(crossTexture, []);
  const marks = useMemo(() => {
    const m = [];
    for (let x = -13; x <= 13; x += 2.2) for (let y = 1; y <= 6.2; y += 1.7) m.push([x, y]);
    return m;
  }, []);
  return (
    <group>
      <mesh position={[0, 3.6, -10]} receiveShadow>
        <planeGeometry args={[30, 7.2]} />
        <meshStandardMaterial color="#2fae57" roughness={0.92} emissive="#123d20" emissiveIntensity={0.12} />
      </mesh>
      <mesh position={[0, 0.01, -9.2]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[30, 1.8]} />
        <meshStandardMaterial color="#2ba151" roughness={0.95} />
      </mesh>
      {marks.map(([x, y], i) => (
        <mesh key={i} position={[x, y, -9.96]}>
          <planeGeometry args={[0.22, 0.22]} />
          <meshBasicMaterial map={cross} transparent opacity={0.8} />
        </mesh>
      ))}
      <mesh castShadow position={[-7, 1.6, -6.5]} rotation={[0, 0.35, 0]}>
        <boxGeometry args={[2.4, 3.2, 0.08]} />
        <meshStandardMaterial color="#2fae57" roughness={0.95} />
      </mesh>
      <mesh castShadow position={[4.5, 1.4, -7.5]} rotation={[0, -0.2, 0]}>
        <boxGeometry args={[2, 2.8, 0.08]} />
        <meshStandardMaterial color="#2fae57" roughness={0.95} />
      </mesh>
    </group>
  );
}

function LightRig({ position, target, intensity = 300, angle = 0.5, color = "#fff3e0", castShadow = false }) {
  const tgt = useMemo(() => {
    const o = new THREE.Object3D();
    o.position.set(...target);
    return o;
  }, [target]);
  return (
    <group>
      <primitive object={tgt} />
      <spotLight position={position} angle={angle} penumbra={0.55} intensity={intensity} color={color} target={tgt} castShadow={castShadow} shadow-mapSize={[1024, 1024]} shadow-bias={-0.0004} distance={30} />
      <mesh position={position}>
        <cylinderGeometry args={[0.14, 0.2, 0.35, 10]} />
        <meshStandardMaterial color="#15171c" roughness={0.4} metalness={0.6} />
      </mesh>
      {/* drop tube + clamp tying the fixture to the overhead grid */}
      <mesh position={[position[0], position[1] + 0.33, position[2]]}>
        <cylinderGeometry args={[0.035, 0.035, 0.42, 8]} />
        <meshStandardMaterial color="#20232a" roughness={0.45} metalness={0.7} />
      </mesh>
      <mesh position={[position[0], position[1] + 0.56, position[2]]} rotation={[0, Math.PI / 2, 0]}>
        <torusGeometry args={[0.085, 0.022, 6, 14]} />
        <meshStandardMaterial color="#2a2e35" roughness={0.5} metalness={0.7} />
      </mesh>
      <mesh position={[(position[0] + target[0]) / 2, position[1] / 2, (position[2] + target[2]) / 2]}>
        <coneGeometry args={[Math.tan(angle) * position[1] * 0.5, position[1], 24, 1, true]} />
        <meshBasicMaterial color={color} transparent opacity={0.011} side={THREE.DoubleSide} depthWrite={false} blending={THREE.AdditiveBlending} />
      </mesh>
    </group>
  );
}

export function Truss() {
  const bars = [];
  for (let z = -8; z <= 6; z += 3.5) bars.push({ pos: [0, 6.8, z], rot: [0, 0, Math.PI / 2], len: 26 });
  for (let x = -12; x <= 12; x += 6) bars.push({ pos: [x, 6.8, -1], rot: [Math.PI / 2, 0, 0], len: 15 });
  // vertical columns so the whole grid is physically carried to the floor
  const columns = [];
  for (const x of [-13, 13]) for (const z of [-8.4, -1, 5.4]) columns.push([x, z]);
  return (
    <group>
      {bars.map((b, i) => (
        <mesh key={i} position={b.pos} rotation={b.rot} castShadow>
          <cylinderGeometry args={[0.07, 0.07, b.len, 8]} />
          <meshStandardMaterial color="#23262c" roughness={0.5} metalness={0.7} />
        </mesh>
      ))}
      {columns.map(([x, z], i) => (
        <group key={`col-${i}`} position={[x, 0, z]}>
          <mesh castShadow position={[0, 3.4, 0]}>
            <cylinderGeometry args={[0.1, 0.13, 6.8, 10]} />
            <meshStandardMaterial color="#2a2e35" roughness={0.5} metalness={0.7} />
          </mesh>
          {/* diagonal knee brace up to the grid */}
          <mesh castShadow position={[x < 0 ? 0.55 : -0.55, 6.1, 0]} rotation={[0, 0, x < 0 ? -0.72 : 0.72]}>
            <cylinderGeometry args={[0.045, 0.045, 1.6, 8]} />
            <meshStandardMaterial color="#2a2e35" roughness={0.5} metalness={0.7} />
          </mesh>
          {/* base plate */}
          <mesh receiveShadow position={[0, 0.03, 0]}>
            <cylinderGeometry args={[0.36, 0.4, 0.06, 14]} />
            <meshStandardMaterial color="#1b1e24" roughness={0.6} metalness={0.5} />
          </mesh>
        </group>
      ))}
      <LightRig position={[-4, 6.7, -2]} target={[-2, 0, -4]} intensity={420} angle={0.45} castShadow />
      <LightRig position={[4, 6.7, -4]} target={[3, 0, -5.2]} intensity={380} angle={0.4} color="#ffe9c4" castShadow />
      <LightRig position={[0, 6.7, 2]} target={[1.5, 0, 0.5]} intensity={170} angle={0.5} />
      <LightRig position={[-10, 6.7, -2]} target={[-11, 0, -3]} intensity={300} angle={0.42} color="#dfe9ff" />
      <LightRig position={[9, 6.7, 2]} target={[10, 0, 2]} intensity={240} angle={0.4} />
      <LightRig position={[0, 6.9, -7]} target={[0, 2.5, -9.9]} intensity={130} angle={0.6} color="#eaffe0" />
    </group>
  );
}

export function Technocrane({ position = [-2, 0, -4] }) {
  return (
    <group position={position}>
      {[-0.45, 0.45].map((z, i) => (
        <mesh key={i} castShadow position={[0, 0.05, z]}>
          <boxGeometry args={[4.5, 0.1, 0.14]} />
          <meshStandardMaterial color="#3a3e45" metalness={0.8} roughness={0.35} />
        </mesh>
      ))}
      <mesh castShadow position={[0, 0.35, 0]}>
        <boxGeometry args={[1.5, 0.5, 1.2]} />
        <meshStandardMaterial color="#191c22" metalness={0.6} roughness={0.4} />
      </mesh>
      <mesh castShadow position={[0, 1.1, 0]}>
        <cylinderGeometry args={[0.22, 0.3, 1.2, 12]} />
        <meshStandardMaterial color="#22252c" metalness={0.7} roughness={0.35} />
      </mesh>
      <group position={[0, 1.75, 0]} rotation={[0, -0.5, 0]}>
        <group rotation={[0, 0, 0.2]}>
          <mesh castShadow position={[2.3, 0, 0]}>
            <boxGeometry args={[4.8, 0.22, 0.16]} />
            <meshStandardMaterial color="#15181e" metalness={0.75} roughness={0.3} />
          </mesh>
          <mesh castShadow position={[-1.1, 0, 0]}>
            <boxGeometry args={[1, 0.5, 0.4]} />
            <meshStandardMaterial color="#0e1013" metalness={0.5} roughness={0.5} />
          </mesh>
          {/* rear beam joining the counterweight block to the main arm */}
          <mesh castShadow position={[-0.72, 0, 0]}>
            <boxGeometry args={[1.3, 0.2, 0.15]} />
            <meshStandardMaterial color="#15181e" metalness={0.75} roughness={0.3} />
          </mesh>
          {/* camera head panned and tilted onto the actors on the sand set */}
          <group position={[4.7, 0.25, 0]} rotation={[-0.39, 1.83, 0]}>
            <mesh castShadow>
              <boxGeometry args={[0.65, 0.4, 0.4]} />
              <meshStandardMaterial color="#101216" metalness={0.6} roughness={0.35} />
            </mesh>
            {/* twin film magazines on top: the classic "mouse ears" */}
            {[-0.19, 0.1].map((x, i) => (
              <group key={i} position={[x, 0.42, 0]} rotation={[Math.PI / 2, 0, 0]}>
                <mesh castShadow>
                  <cylinderGeometry args={[0.23, 0.23, 0.11, 26]} />
                  <meshStandardMaterial color="#14171c" metalness={0.55} roughness={0.42} />
                </mesh>
                <mesh position={[0, 0.058, 0]}>
                  <cylinderGeometry args={[0.185, 0.185, 0.012, 24]} />
                  <meshStandardMaterial color="#23272e" metalness={0.6} roughness={0.35} />
                </mesh>
                <mesh position={[0, -0.058, 0]}>
                  <cylinderGeometry args={[0.185, 0.185, 0.012, 24]} />
                  <meshStandardMaterial color="#23272e" metalness={0.6} roughness={0.35} />
                </mesh>
                <mesh position={[0, 0.062, 0]}>
                  <cylinderGeometry args={[0.035, 0.035, 0.02, 12]} />
                  <meshStandardMaterial color="#8d939c" metalness={0.8} roughness={0.3} />
                </mesh>
              </group>
            ))}
            <mesh castShadow position={[0.42, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
              <cylinderGeometry args={[0.09, 0.11, 0.35, 14]} />
              <meshStandardMaterial color="#08090b" metalness={0.8} roughness={0.25} />
            </mesh>
            <mesh castShadow position={[0.3, 0.02, 0.3]}>
              <boxGeometry args={[0.34, 0.2, 0.22]} />
              <meshStandardMaterial color="#14161b" roughness={0.5} />
            </mesh>
          </group>
        </group>
      </group>
    </group>
  );
}

export function VideoVillage({ position = [5, 0, -0.5] }) {
  const mon1 = useMemo(() => monitorTexture("REC · CAM A", "4K 23.976 · T2.8"), []);
  const mon2 = useMemo(() => monitorTexture("PLAYBACK", "CLIP 042 · TAKE 3"), []);
  return (
    <group position={position} rotation={[0, -0.9, 0]}>
      <mesh castShadow position={[0, 0.74, 0]}>
        <boxGeometry args={[1.9, 0.07, 0.85]} />
        <meshStandardMaterial color="#1a1d23" roughness={0.6} />
      </mesh>
      {[[-0.85, -0.35], [0.85, -0.35], [-0.85, 0.35], [0.85, 0.35]].map(([x, z], i) => (
        <mesh key={i} position={[x, 0.37, z]}>
          <cylinderGeometry args={[0.03, 0.03, 0.74, 8]} />
          <meshStandardMaterial color="#2b2f36" metalness={0.6} roughness={0.4} />
        </mesh>
      ))}
      {[mon1, mon2].map((tex, i) => (
        <group key={i} position={[i === 0 ? -0.45 : 0.45, 1.15, -0.18]}>
          <mesh castShadow>
            <boxGeometry args={[0.62, 0.4, 0.05]} />
            <meshStandardMaterial color="#0c0e11" roughness={0.4} metalness={0.5} />
          </mesh>
          <mesh position={[0, 0, 0.028]}>
            <planeGeometry args={[0.56, 0.34]} />
            <meshBasicMaterial map={tex} toneMapped={false} />
          </mesh>
        </group>
      ))}
      {[-0.45, 0.45].map((x, i) => (
        <group key={i} position={[x, 0, 0.75]}>
          {/* wooden director stool: seat, backrest and four legs on the floor */}
          <mesh castShadow position={[0, 0.4475, 0]}>
            <boxGeometry args={[0.48, 0.055, 0.44]} />
            <meshStandardMaterial color="#7a5330" roughness={0.78} />
          </mesh>
          {[[-0.19, -0.17], [0.19, -0.17], [-0.19, 0.17], [0.19, 0.17]].map(([lx, lz], k) => (
            <mesh key={k} castShadow position={[lx, 0.215, lz]}>
              <cylinderGeometry args={[0.022, 0.026, 0.43, 8]} />
              <meshStandardMaterial color="#6b4626" roughness={0.85} />
            </mesh>
          ))}
          {[-0.17, 0.17].map((bz) => (
            <mesh key={bz} position={[0, 0.16, bz]} rotation={[0, 0, Math.PI / 2]}>
              <cylinderGeometry args={[0.015, 0.015, 0.38, 6]} />
              <meshStandardMaterial color="#6b4626" roughness={0.85} />
            </mesh>
          ))}
          {/* back uprights + canvas back */}
          {[-0.2, 0.2].map((bx) => (
            <mesh key={bx} castShadow position={[bx, 0.66, 0.19]}>
              <cylinderGeometry args={[0.019, 0.019, 0.44, 8]} />
              <meshStandardMaterial color="#6b4626" roughness={0.85} />
            </mesh>
          ))}
          <mesh castShadow position={[0, 0.8, 0.19]}>
            <boxGeometry args={[0.44, 0.19, 0.035]} />
            <meshStandardMaterial color="#2c3b52" roughness={0.95} />
          </mesh>
        </group>
      ))}
    </group>
  );
}

function Hanger() {
  return (
    <group>
      <mesh position={[0, 0.075, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.032, 0.0055, 6, 14, Math.PI * 1.5]} />
        <meshStandardMaterial color="#9aa0a8" metalness={0.85} roughness={0.3} />
      </mesh>
      <mesh position={[0, 0.022, 0]}>
        <cylinderGeometry args={[0.005, 0.005, 0.06, 6]} />
        <meshStandardMaterial color="#9aa0a8" metalness={0.85} roughness={0.3} />
      </mesh>
      {[-1, 1].map((sx) => (
        <mesh key={sx} position={[sx * 0.075, -0.022, 0]} rotation={[0, 0, sx * 1.22]}>
          <cylinderGeometry args={[0.005, 0.005, 0.165, 6]} />
          <meshStandardMaterial color="#9aa0a8" metalness={0.85} roughness={0.3} />
        </mesh>
      ))}
      <mesh position={[0, -0.078, 0]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.005, 0.005, 0.3, 6]} />
        <meshStandardMaterial color="#9aa0a8" metalness={0.85} roughness={0.3} />
      </mesh>
    </group>
  );
}

function Garment({ kind, color, accent }) {
  const mat = <meshStandardMaterial color={color} roughness={0.96} />;
  if (kind === "trousers") {
    return (
      <group position={[0, -0.14, 0]}>
        <mesh castShadow position={[0, 0.02, 0]}>
          <boxGeometry args={[0.3, 0.11, 0.09]} />
          {mat}
        </mesh>
        {[-1, 1].map((sx) => (
          <mesh key={sx} castShadow position={[sx * 0.075, -0.39, 0]} rotation={[0, 0, sx * 0.02]}>
            <boxGeometry args={[0.135, 0.7, 0.082]} />
            {mat}
          </mesh>
        ))}
      </group>
    );
  }
  if (kind === "jacket") {
    return (
      <group position={[0, -0.12, 0]}>
        <mesh castShadow position={[0, -0.28, 0]}>
          <boxGeometry args={[0.38, 0.6, 0.12]} />
          {mat}
        </mesh>
        <mesh castShadow position={[0, -0.02, 0]}>
          <boxGeometry args={[0.34, 0.11, 0.115]} />
          {mat}
        </mesh>
        {[-1, 1].map((sx) => (
          <mesh key={sx} castShadow position={[sx * 0.225, -0.3, 0]} rotation={[0, 0, sx * 0.05]}>
            <boxGeometry args={[0.115, 0.56, 0.1]} />
            {mat}
          </mesh>
        ))}
        {[-1, 1].map((sx) => (
          <mesh key={`l${sx}`} position={[sx * 0.065, -0.12, 0.062]} rotation={[0, 0, sx * 0.3]}>
            <boxGeometry args={[0.07, 0.24, 0.012]} />
            <meshStandardMaterial color={accent} roughness={0.85} />
          </mesh>
        ))}
      </group>
    );
  }
  if (kind === "dress") {
    return (
      <group position={[0, -0.12, 0]}>
        <mesh castShadow position={[0, -0.16, 0]}>
          <boxGeometry args={[0.28, 0.34, 0.1]} />
          {mat}
        </mesh>
        <mesh castShadow position={[0, -0.58, 0]}>
          <cylinderGeometry args={[0.15, 0.3, 0.55, 14, 1, true]} />
          <meshStandardMaterial color={color} roughness={0.96} side={THREE.DoubleSide} />
        </mesh>
      </group>
    );
  }
  // shirt / t-shirt
  return (
    <group position={[0, -0.12, 0]}>
      <mesh castShadow position={[0, -0.24, 0]}>
        <boxGeometry args={[0.33, 0.48, 0.1]} />
        {mat}
      </mesh>
      {[-1, 1].map((sx) => (
        <mesh key={sx} castShadow position={[sx * 0.215, -0.07, 0]} rotation={[0, 0, sx * 0.22]}>
          <boxGeometry args={[0.13, 0.2, 0.095]} />
          {mat}
        </mesh>
      ))}
      <mesh position={[0, 0.005, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.055, 0.016, 6, 14]} />
        <meshStandardMaterial color={accent} roughness={0.9} />
      </mesh>
    </group>
  );
}

const RACK_ITEMS = [
  { kind: "shirt", color: "#6e4a26", accent: "#8f6535" },
  { kind: "jacket", color: "#2f3a55", accent: "#1d2537" },
  { kind: "trousers", color: "#3b2d1e" },
  { kind: "shirt", color: "#7a2020", accent: "#9c3232" },
  { kind: "dress", color: "#6b5a2a" },
  { kind: "jacket", color: "#54341c", accent: "#3a2312" },
  { kind: "trousers", color: "#22262e" },
  { kind: "shirt", color: "#4c5f55", accent: "#69806f" },
];

export function CostumeRacks({ position = [10, 0, 2] }) {
  const sign = useMemo(() => textTexture({ text: "REPARTO COSTUMI", w: 1024, h: 160, bg: "#14100a", color: "#f2c069", font: "900 96px Arial", border: "#7a5a24" }), []);
  return (
    <group position={position} rotation={[0, 0.5, 0]}>
      {[0, 1].map((r) => (
        <group key={r} position={[0, 0, r * 1.4]}>
          {[-1.2, 1.2].map((x, i) => (
            <mesh key={i} castShadow position={[x, 0.95, 0]}>
              <cylinderGeometry args={[0.035, 0.05, 1.9, 8]} />
              <meshStandardMaterial color="#33373e" metalness={0.7} roughness={0.35} />
            </mesh>
          ))}
          <mesh castShadow position={[0, 1.85, 0]} rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[0.03, 0.03, 2.5, 8]} />
            <meshStandardMaterial color="#33373e" metalness={0.7} roughness={0.35} />
          </mesh>
          {RACK_ITEMS.map((it, i) => (
            <group key={i} position={[-1.05 + i * 0.3, 1.77, r === 1 ? 0.02 : -0.02]} rotation={[0, ((i + r) % 3 - 1) * 0.16, 0]}>
              <Hanger />
              <Garment kind={r === 1 ? RACK_ITEMS[(i + 3) % RACK_ITEMS.length].kind : it.kind} color={it.color} accent={it.accent || "#2a2a2a"} />
            </group>
          ))}
        </group>
      ))}
      {/* 3D department sign */}
      <group position={[0, 2.78, 2.75]}>
        <mesh castShadow>
          <boxGeometry args={[2.5, 0.52, 0.09]} />
          <meshStandardMaterial color="#241a0e" roughness={0.85} />
        </mesh>
        <mesh position={[0, 0, 0.05]}>
          <planeGeometry args={[2.36, 0.4]} />
          <meshBasicMaterial map={sign} toneMapped={false} />
        </mesh>
        <mesh position={[0, 0, -0.05]} rotation={[0, Math.PI, 0]}>
          <planeGeometry args={[2.36, 0.4]} />
          <meshBasicMaterial map={sign} toneMapped={false} />
        </mesh>
        {[-0.9, 0.9].map((x) => (
          <mesh key={x} castShadow position={[x, -1.52, 0]}>
            <cylinderGeometry args={[0.03, 0.038, 2.52, 10]} />
            <meshStandardMaterial color="#33373e" metalness={0.7} roughness={0.4} />
          </mesh>
        ))}
        {[-0.9, 0.9].map((x) => (
          <mesh key={`f${x}`} position={[x, -2.76, 0]}>
            <cylinderGeometry args={[0.11, 0.13, 0.04, 12]} />
            <meshStandardMaterial color="#1b1e24" metalness={0.5} roughness={0.6} />
          </mesh>
        ))}
      </group>
      <spotLight position={[0, 3.2, 1.4]} angle={0.6} penumbra={0.7} intensity={90} color="#ffe1b3" />
    </group>
  );
}

export function StuntZone({ position = [-11, 0, -3] }) {
  const danger = useMemo(dangerTexture, []);
  const fire = useRef();
  const flame = useRef();
  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    if (fire.current) fire.current.intensity = 40 + Math.sin(t * 11) * 12 + Math.sin(t * 23) * 6;
    if (flame.current) {
      flame.current.scale.y = 1 + Math.sin(t * 9) * 0.18;
      flame.current.rotation.y = t * 1.5;
    }
  });
  return (
    <group position={position}>
      {[[0, 0.16, 0], [2.1, 0.16, 0.3], [1, 0.46, -0.1]].map((p, i) => (
        <mesh key={i} castShadow receiveShadow position={p}>
          <boxGeometry args={[2, 0.3, 1.5]} />
          <meshStandardMaterial color={i === 2 ? "#274b8f" : "#1d3a75"} roughness={0.9} />
        </mesh>
      ))}
      {[[-2.4, -2.6], [-1.7, -2.2], [-2.1, -3.3]].map(([x, z], i) => (
        <mesh key={i} castShadow position={[x, 0.45, z]}>
          <cylinderGeometry args={[0.3, 0.3, 0.9, 16]} />
          <meshStandardMaterial map={danger} roughness={0.7} />
        </mesh>
      ))}
      <group position={[-3.4, 0, -1.6]}>
        <mesh castShadow position={[0, 0.06, 0]}>
          <cylinderGeometry args={[0.5, 0.55, 0.12, 16]} />
          <meshStandardMaterial color="#0d0d0f" roughness={0.9} />
        </mesh>
        <mesh ref={flame} position={[0, 0.5, 0]}>
          <coneGeometry args={[0.32, 0.9, 10]} />
          <meshBasicMaterial color="#ff7a1a" transparent opacity={0.85} blending={THREE.AdditiveBlending} depthWrite={false} />
        </mesh>
        <mesh position={[0, 0.32, 0]}>
          <coneGeometry args={[0.2, 0.5, 10]} />
          <meshBasicMaterial color="#ffd23f" transparent opacity={0.9} blending={THREE.AdditiveBlending} depthWrite={false} />
        </mesh>
        <pointLight ref={fire} position={[0, 0.8, 0]} color="#ff8c2e" intensity={40} distance={9} />
      </group>
    </group>
  );
}

// --- cork board with real press cuttings and photos pinned on it ---
let corkTex = null;
function getCorkTexture() {
  if (corkTex) return corkTex;
  const c = document.createElement("canvas");
  c.width = 768; c.height = 512;
  const g = c.getContext("2d");
  g.fillStyle = "#9c7047";
  g.fillRect(0, 0, 768, 512);
  for (let i = 0; i < 90000; i++) {
    const r = Math.random();
    g.fillStyle = r < 0.36 ? "rgba(74,48,26,0.18)" : r < 0.72 ? "rgba(186,148,106,0.2)" : "rgba(126,92,58,0.16)";
    const s2 = 0.6 + Math.random() * 1.6;
    g.beginPath();
    g.ellipse(Math.random() * 768, Math.random() * 512, s2, s2 * (0.6 + Math.random() * 0.8), Math.random() * 3, 0, 6.283);
    g.fill();
  }
  g.fillStyle = "rgba(40,24,10,0.1)";
  for (let i = 0; i < 700; i++) g.fillRect(Math.random() * 768, Math.random() * 512, 1, 1 + Math.random() * 2);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  corkTex = t;
  return t;
}

const PINS = ["#c62828", "#f9a825", "#1e88e5", "#2e7d32", "#ececec"];

// [url, width, height, x, y, tilt]
const CUTTINGS = [
  ["/press/art1.jpg", 1.45, 1.3, -1.16, 0.58, 0.014],
  ["/press/art2.jpg", 1.5, 0.95, -1.12, -0.79, -0.018],
  ["/press/art3.jpg", 0.95, 1.19, 0.0, 0.62, -0.012],
  ["/press/art4.jpg", 0.9, 1.23, -0.06, -0.72, 0.016],
  ["/press/portrait-red.jpg", 0.6, 0.9, 0.72, 0.76, -0.026],
  ["/press/se-16.jpg", 0.6, 0.9, 0.72, -0.26, 0.022],
  ["/press/se-11.jpg", 0.72, 1.08, 1.5, 0.56, 0.019],
  ["/press/se-20.jpg", 0.6, 0.9, 1.45, -0.74, -0.024],
];

function Cutting({ url, w, h, x, y, tilt, pin }) {
  const tex = usePhotoTexture(url);
  return (
    <group position={[x, y, 0.052]} rotation={[0, 0, tilt]}>
      {/* soft contact shadow */}
      <mesh position={[0.012, -0.014, -0.004]}>
        <planeGeometry args={[w + 0.05, h + 0.05]} />
        <meshBasicMaterial color="#3a2512" transparent opacity={0.45} />
      </mesh>
      {/* paper backing */}
      <mesh>
        <planeGeometry args={[w + 0.035, h + 0.035]} />
        <meshStandardMaterial color="#efe9dc" roughness={0.95} />
      </mesh>
      {/* the cutting itself */}
      <mesh position={[0, 0, 0.002]}>
        <planeGeometry args={[w, h]} />
        <meshStandardMaterial map={tex} roughness={0.9} />
      </mesh>
      {/* push pin */}
      <group position={[0, h / 2 - 0.01, 0.012]}>
        <mesh castShadow>
          <sphereGeometry args={[0.022, 12, 10]} />
          <meshStandardMaterial color={pin} roughness={0.25} metalness={0.15} />
        </mesh>
        <mesh position={[0, 0, -0.012]}>
          <cylinderGeometry args={[0.005, 0.005, 0.03, 6]} />
          <meshStandardMaterial color="#8d939c" metalness={0.8} roughness={0.3} />
        </mesh>
      </group>
    </group>
  );
}

export function PressBoard({ position = [-17, 0, 2] }) {
  const cork = useMemo(getCorkTexture, []);
  const title = useMemo(() => textTexture({ text: "RASSEGNA STAMPA", w: 1024, h: 160, bg: "#14100a", color: "#f2c069", font: "900 72px Arial", border: "#7a5a24" }), []);
  return (
    <group position={position} rotation={[0, Math.PI / 2, 0]}>
      {/* support posts carrying the board down to the floor */}
      {[-2.0, 2.0].map((x) => (
        <group key={`post-${x}`} position={[x, 0, -0.12]}>
          <mesh castShadow position={[0, 1.6, 0]}>
            <cylinderGeometry args={[0.055, 0.07, 3.2, 10]} />
            <meshStandardMaterial color="#3a3520" roughness={0.8} metalness={0.2} />
          </mesh>
          <mesh receiveShadow position={[0, 0.025, 0]}>
            <cylinderGeometry args={[0.2, 0.24, 0.05, 14]} />
            <meshStandardMaterial color="#1b1e24" metalness={0.5} roughness={0.6} />
          </mesh>
          {/* diagonal strut into the frame */}
          <mesh castShadow position={[x > 0 ? -0.22 : 0.22, 0.55, 0.06]} rotation={[0, 0, x > 0 ? -0.5 : 0.5]}>
            <cylinderGeometry args={[0.03, 0.03, 0.75, 8]} />
            <meshStandardMaterial color="#3a3520" roughness={0.8} />
          </mesh>
        </group>
      ))}
      {/* wooden frame */}
      <mesh castShadow position={[0, 1.95, 0]}>
        <boxGeometry args={[4.7, 3.2, 0.1]} />
        <meshStandardMaterial color="#4a3520" roughness={0.85} />
      </mesh>
      {/* cork surface */}
      <mesh receiveShadow position={[0, 1.95, 0.052]}>
        <planeGeometry args={[4.4, 2.9]} />
        <meshStandardMaterial map={cork} roughness={0.95} />
      </mesh>
      <group position={[0, 1.95, 0]}>
        {CUTTINGS.map(([url, w, h, x, y, tilt], i) => (
          <Cutting key={url} url={url} w={w} h={h} x={x} y={y} tilt={tilt} pin={PINS[i % PINS.length]} />
        ))}
      </group>
      {/* department-style sign board, same as the other set signs */}
      <group position={[0, 3.78, 0.02]}>
        <mesh castShadow>
          <boxGeometry args={[2.5, 0.52, 0.09]} />
          <meshStandardMaterial color="#241a0e" roughness={0.85} />
        </mesh>
        <mesh position={[0, 0, 0.05]}>
          <planeGeometry args={[2.36, 0.4]} />
          <meshBasicMaterial map={title} toneMapped={false} />
        </mesh>
        <mesh position={[0, 0, -0.05]} rotation={[0, Math.PI, 0]}>
          <planeGeometry args={[2.36, 0.4]} />
          <meshBasicMaterial map={title} toneMapped={false} />
        </mesh>
      </group>
      <spotLight position={[0, 4.4, 2.2]} angle={0.62} penumbra={0.6} intensity={110} color="#fff1d6" />
    </group>
  );
}

// --- real photographs and film posters hung along the studio walls ---
function Framed({ url, w, h, position, rotation, tint = "#0b0c0f" }) {
  const tex = usePhotoTexture(url);
  return (
    <group position={position} rotation={rotation}>
      <mesh castShadow>
        <boxGeometry args={[w + 0.14, h + 0.14, 0.07]} />
        <meshStandardMaterial color={tint} roughness={0.45} metalness={0.25} />
      </mesh>
      <mesh position={[0, 0, 0.038]}>
        <planeGeometry args={[w + 0.06, h + 0.06]} />
        <meshStandardMaterial color="#e7e2d8" roughness={0.9} />
      </mesh>
      <mesh position={[0, 0, 0.042]}>
        <planeGeometry args={[w, h]} />
        <meshStandardMaterial map={tex} emissiveMap={tex} emissive="#ffffff" emissiveIntensity={0.42} roughness={0.85} />
      </mesh>
    </group>
  );
}

const LEFT = [0, Math.PI / 2, 0];
const RIGHT = [0, -Math.PI / 2, 0];
const BACK = [0, 0, 0];
const FRONT = [0, Math.PI, 0];

const WALL_ART = [
  // left wall — behind the scenes photographs
  { url: "/wall/w1.jpg", w: 2.25, h: 1.5, position: [-18.78, 2.75, -4], rotation: LEFT },
  { url: "/wall/w4.jpg", w: 1.5, h: 1.5, position: [-18.78, 2.75, -7.4], rotation: LEFT },
  // left wall — posters
  { url: "/posters/film/nessun-domani.jpg", w: 1.2, h: 1.7, position: [-18.78, 2.85, 5.4], rotation: LEFT },
  { url: "/posters/film/equilibrista.jpg", w: 1.08, h: 1.7, position: [-18.78, 2.85, 7.15], rotation: LEFT },
  // back wall — only outside the green screen
  { url: "/posters/film/zoolander2.jpg", w: 1.1, h: 1.7, position: [-17, 3, -10.82], rotation: BACK },
  { url: "/posters/film/racconto.jpg", w: 1.19, h: 1.7, position: [17, 3, -10.82], rotation: BACK },
  // right wall
  { url: "/wall/w2.jpg", w: 1.5, h: 1.0, position: [18.78, 2.75, -8.6], rotation: RIGHT },
  { url: "/posters/film/io-non-sono-cosi.jpg", w: 1.2, h: 1.7, position: [18.78, 2.85, 0.7], rotation: RIGHT },
  { url: "/posters/film/origami.jpg", w: 1.2, h: 1.7, position: [18.78, 2.85, 2.7], rotation: RIGHT },
  { url: "/posters/film/la-priere.jpg", w: 1.2, h: 1.7, position: [18.78, 2.85, 4.7], rotation: RIGHT },
  // front wall
  { url: "/wall/w3.jpg", w: 1.79, h: 1.0, position: [-6.5, 2.75, 8.82], rotation: FRONT },
  { url: "/posters/film/nessuno-come-noi.jpg", w: 1.19, h: 1.7, position: [3.6, 2.85, 8.82], rotation: FRONT },
  { url: "/posters/un-tuffo-nel-temporale.jpg", w: 1.21, h: 1.7, position: [5.5, 2.85, 8.82], rotation: FRONT },
  { url: "/posters/film/tre-storie.jpg", w: 1.13, h: 1.7, position: [-9.6, 2.85, 8.82], rotation: FRONT },
  // second batch of real photographs
  { url: "/wall/w5.jpg", w: 2.0, h: 1.5, position: [-18.78, 2.75, -9.6], rotation: LEFT },
  { url: "/wall/w6.jpg", w: 2.1, h: 1.39, position: [18.78, 2.75, 6.8], rotation: RIGHT },
  { url: "/wall/w7.jpg", w: 1.86, h: 1.4, position: [-3.0, 2.75, 8.82], rotation: FRONT },
  { url: "/wall/w8.jpg", w: 1.45, h: 1.44, position: [-13.2, 2.75, 8.82], rotation: FRONT },
  { url: "/wall/w9.jpg", w: 2.0, h: 1.5, position: [8.6, 2.75, 8.82], rotation: FRONT },
];

export function WallStills() {
  return (
    <group>
      {WALL_ART.map((a, i) => (
        <Framed key={i} {...a} />
      ))}
    </group>
  );
}

export function Corridor() {
  const signP = useMemo(() => textTexture({ text: "PRODUZIONE", w: 512, h: 128, bg: "#14161c", color: "#e8b04a", font: "900 64px Arial", border: "#3a3325" }), []);
  const signM = useMemo(() => textTexture({ text: "MAGAZZINO", w: 512, h: 128, bg: "#14161c", color: "#e8b04a", font: "900 64px Arial", border: "#3a3325" }), []);
  const signC = useMemo(() => textTexture({ text: "CINEMA", w: 512, h: 160, bg: "#12060a", color: "#ffb340", font: "900 84px Georgia", border: "#d97706" }), []);
  const bulbMat = useMemo(() => new THREE.MeshBasicMaterial({ color: "#ffd27a" }), []);
  return (
    <group>
      {/* corridor walls */}
      <mesh castShadow receiveShadow position={[22, 1.7, -6]}>
        <boxGeometry args={[6.6, 3.4, 0.25]} />
        <meshStandardMaterial color="#2a2c31" roughness={0.9} />
      </mesh>
      <mesh castShadow receiveShadow position={[22, 1.7, -2]}>
        <boxGeometry args={[6.6, 3.4, 0.25]} />
        <meshStandardMaterial color="#2a2c31" roughness={0.9} />
      </mesh>
      <mesh position={[22, 3.35, -4]} rotation={[Math.PI / 2, 0, 0]}>
        <planeGeometry args={[6.6, 4.4]} />
        <meshStandardMaterial color="#17181c" roughness={1} />
      </mesh>
      {/* production door */}
      <group position={[21, 0, -5.86]}>
        <mesh castShadow position={[0, 1.1, 0]}>
          <boxGeometry args={[1.1, 2.2, 0.08]} />
          <meshStandardMaterial color="#4a3b26" roughness={0.7} />
        </mesh>
        <mesh position={[0, 1.62, 0.05]}>
          <planeGeometry args={[0.95, 0.24]} />
          <meshBasicMaterial map={signP} toneMapped={false} />
        </mesh>
      </group>
      {/* warehouse door */}
      <group position={[23, 0, -1.86]} rotation={[0, Math.PI, 0]}>
        <mesh castShadow position={[0, 1.1, 0]}>
          <boxGeometry args={[1.1, 2.2, 0.08]} />
          <meshStandardMaterial color="#3d3a33" roughness={0.7} />
        </mesh>
        <mesh position={[0, 1.62, 0.05]}>
          <planeGeometry args={[0.95, 0.24]} />
          <meshBasicMaterial map={signM} toneMapped={false} />
        </mesh>
      </group>
      {/* lamps */}
      {[[20.5, -5.8], [23.5, -5.8], [21.5, -2.2]].map(([x, z], i) => (
        <group key={i} position={[x, 2.6, z]}>
          {/* stem + shade up to the corridor ceiling */}
          <mesh position={[0, 0.3, 0]}>
            <cylinderGeometry args={[0.014, 0.014, 0.6, 6]} />
            <meshStandardMaterial color="#20232a" roughness={0.6} metalness={0.5} />
          </mesh>
          <mesh position={[0, 0.12, 0]}>
            <coneGeometry args={[0.16, 0.14, 14, 1, true]} />
            <meshStandardMaterial color="#2c3036" roughness={0.6} metalness={0.4} side={THREE.DoubleSide} />
          </mesh>
          <mesh material={bulbMat}>
            <sphereGeometry args={[0.07, 10, 10]} />
          </mesh>
          <pointLight color="#ffc76a" intensity={7} distance={6} />
        </group>
      ))}
      {/* cinema sign hanging halfway down the corridor, clear of the EXIT box */}
      <group position={[22, 2.72, -4]}>
        {/* housing sits between the two faces, never in front of them */}
        <mesh position={[0.05, 0, 0]}>
          <boxGeometry args={[0.07, 0.86, 2.52]} />
          <meshStandardMaterial color="#1a0d10" roughness={0.8} />
        </mesh>
        <mesh position={[0.005, 0, 0]} rotation={[0, -Math.PI / 2, 0]}>
          <planeGeometry args={[2.4, 0.75]} />
          <meshBasicMaterial map={signC} toneMapped={false} />
        </mesh>
        <mesh position={[0.095, 0, 0]} rotation={[0, Math.PI / 2, 0]}>
          <planeGeometry args={[2.4, 0.75]} />
          <meshBasicMaterial map={signC} toneMapped={false} />
        </mesh>
        {[-1, 1].map((s) => (
          <mesh key={s} position={[0.03, 0.52, s]}>
            <cylinderGeometry args={[0.018, 0.018, 0.65, 8]} />
            <meshStandardMaterial color="#2a2e35" metalness={0.7} roughness={0.4} />
          </mesh>
        ))}
      </group>
      <pointLight position={[21.4, 2.7, -4]} color="#ffb340" intensity={12} distance={6} />
    </group>
  );
}

const SEAT_POSITIONS = (() => {
  const out = [];
  for (let r = 0; r < 12; r++) {
    const x = 27.5 + r * 1.18;
    for (let i = 0; i < 17; i++) {
      const z = -11.3 + i * 0.92;
      if (Math.abs(z + 0.5) < 0.85) continue; // aisle
      out.push([x, z]);
    }
  }
  return out;
})();

export function CinemaRoom() {
  const exitTex = useMemo(() => textTexture({ text: "EXIT", w: 256, h: 96, bg: "#03130a", color: "#2ee06f", font: "900 64px Arial" }), []);
  const baseRef = useRef();
  const backRef = useRef();
  const armRef = useRef();
  const footRef = useRef();

  useEffect(() => {
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, 0, 0));
    const col = new THREE.Color();
    SEAT_POSITIONS.forEach(([x, z], i) => {
      m.compose(new THREE.Vector3(x, 0.25, z), q, new THREE.Vector3(1, 1, 1));
      baseRef.current.setMatrixAt(i, m);
      const jitter = 0.85 + ((i * 37) % 10) / 40;
      baseRef.current.setColorAt(i, col.setRGB(0.35 * jitter, 0.08 * jitter, 0.09 * jitter));
      m.compose(new THREE.Vector3(x - 0.26, 0.6, z), new THREE.Quaternion().setFromEuler(new THREE.Euler(0, 0, 0.08)), new THREE.Vector3(1, 1, 1));
      backRef.current.setMatrixAt(i, m);
      backRef.current.setColorAt(i, col.setRGB(0.3 * jitter, 0.07 * jitter, 0.08 * jitter));
      m.compose(new THREE.Vector3(x, 0.47, z - 0.27), q, new THREE.Vector3(1, 1, 1));
      armRef.current.setMatrixAt(i * 2, m);
      m.compose(new THREE.Vector3(x, 0.47, z + 0.27), q, new THREE.Vector3(1, 1, 1));
      armRef.current.setMatrixAt(i * 2 + 1, m);
      // cast-iron pedestals bolted to the floor
      m.compose(new THREE.Vector3(x, 0.06, z - 0.2), q, new THREE.Vector3(1, 1, 1));
      footRef.current.setMatrixAt(i * 2, m);
      m.compose(new THREE.Vector3(x, 0.06, z + 0.2), q, new THREE.Vector3(1, 1, 1));
      footRef.current.setMatrixAt(i * 2 + 1, m);
    });
    baseRef.current.instanceMatrix.needsUpdate = true;
    backRef.current.instanceMatrix.needsUpdate = true;
    armRef.current.instanceMatrix.needsUpdate = true;
    footRef.current.instanceMatrix.needsUpdate = true;
  }, []);

  return (
    <group>
      {/* walls */}
      <mesh receiveShadow position={[45, 3.5, -4]} rotation={[0, -Math.PI / 2, 0]}>
        <planeGeometry args={[18, 7]} />
        <meshStandardMaterial color="#241417" roughness={0.95} />
      </mesh>
      <mesh receiveShadow position={[25, 3.5, -9.5]} rotation={[0, Math.PI / 2, 0]}>
        <planeGeometry args={[7, 7]} />
        <meshStandardMaterial color="#1c1216" roughness={0.95} />
      </mesh>
      <mesh receiveShadow position={[25, 3.5, 1.5]} rotation={[0, Math.PI / 2, 0]}>
        <planeGeometry args={[7, 7]} />
        <meshStandardMaterial color="#1c1216" roughness={0.95} />
      </mesh>
      <mesh receiveShadow position={[35, 3.5, -13]} rotation={[0, 0, 0]}>
        <planeGeometry args={[20, 7]} />
        <meshStandardMaterial color="#1c1216" roughness={0.95} />
      </mesh>
      <mesh receiveShadow position={[35, 3.5, 5]} rotation={[0, Math.PI, 0]}>
        <planeGeometry args={[20, 7]} />
        <meshStandardMaterial color="#1c1216" roughness={0.95} />
      </mesh>
      <mesh position={[35, 6.2, -4]} rotation={[Math.PI / 2, 0, 0]}>
        <planeGeometry args={[20, 18]} />
        <meshStandardMaterial color="#120c0e" roughness={1} />
      </mesh>
      {/* lintel closing the wall above the corridor opening: the stage roof
          must never be visible from inside the auditorium */}
      <mesh receiveShadow position={[25, 5.2, -4]} rotation={[0, Math.PI / 2, 0]}>
        <planeGeometry args={[4, 3.6]} />
        <meshStandardMaterial color="#1c1216" roughness={0.95} />
      </mesh>
      {/* carpet */}
      <mesh receiveShadow position={[35, 0.02, -4]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[20, 18]} />
        <meshStandardMaterial color="#2a0f12" roughness={1} />
      </mesh>
      {/* screen */}
      <mesh castShadow position={[44.55, 3, -4]}>
        <boxGeometry args={[0.25, 5, 11.2]} />
        <meshStandardMaterial color="#0a0a0c" roughness={0.6} />
      </mesh>
      {/* projection surface backing: the looping trailer (CinemaScreen3D) sits
          just in front of it as a real WebGL plane */}
      <mesh position={[44.42, 3, -4]} rotation={[0, -Math.PI / 2, 0]}>
        <planeGeometry args={[9.4, 4.8]} />
        <meshBasicMaterial color="#05060a" toneMapped={false} />
      </mesh>
      {/* dim house lights */}
      {[[30, -4], [35, -8], [35, 0], [40, -4]].map(([x, z], i) => (
        <pointLight key={`house-${i}`} position={[x, 5.6, z]} color="#ffd9b0" intensity={12} distance={11} />
      ))}
      {/* curtains */}
      {[-10.2, 2.2].map((z, i) => (
        <mesh key={i} castShadow position={[44.3, 2.6, z]} rotation={[0, -Math.PI / 2, 0]}>
          <planeGeometry args={[1.6, 5.2, 8, 1]} />
          <meshStandardMaterial color="#5c1014" roughness={1} side={THREE.DoubleSide} />
        </mesh>
      ))}
      {/* seats */}
      <instancedMesh ref={baseRef} args={[null, null, SEAT_POSITIONS.length]} castShadow receiveShadow key="seat-base">
        <boxGeometry args={[0.55, 0.32, 0.55]} />
        <meshStandardMaterial color="#ffffff" roughness={0.92} />
      </instancedMesh>
      <instancedMesh ref={backRef} args={[null, null, SEAT_POSITIONS.length]} castShadow key="seat-back">
        <boxGeometry args={[0.14, 0.68, 0.55]} />
        <meshStandardMaterial color="#ffffff" roughness={0.92} />
      </instancedMesh>
      <instancedMesh ref={armRef} args={[null, null, SEAT_POSITIONS.length * 2]} key="seat-arms">
        <boxGeometry args={[0.42, 0.09, 0.07]} />
        <meshStandardMaterial color="#1c1512" roughness={0.6} />
      </instancedMesh>
      <instancedMesh ref={footRef} args={[null, null, SEAT_POSITIONS.length * 2]} castShadow key="seat-feet">
        <boxGeometry args={[0.3, 0.12, 0.09]} />
        <meshStandardMaterial color="#17120f" roughness={0.65} metalness={0.3} />
      </instancedMesh>
      {/* sconces */}
      {[[26, -12.7], [31, -12.7], [36, -12.7], [41, -12.7], [26, 4.7], [31, 4.7], [36, 4.7], [41, 4.7]].map(([x, z], i) => (
        <group key={i} position={[x, 3.4, z]}>
          <mesh>
            <sphereGeometry args={[0.09, 8, 8]} />
            <meshBasicMaterial color="#ffcf8a" />
          </mesh>
          <pointLight color="#ffc27a" intensity={9} distance={9} />
        </group>
      ))}
      {/* exit signs: over the entrance and over the side doors */}
      {[
        { pos: [25.12, 3, -4], rot: [0, Math.PI / 2, 0] },
        { pos: [28, 3, -12.85], rot: [0, 0, 0] },
        { pos: [28, 3, 4.85], rot: [0, Math.PI, 0] },
      ].map((e, i) => (
        <group key={`exit-${i}`} position={e.pos} rotation={e.rot}>
          <mesh position={[0, 0, -0.04]}>
            <boxGeometry args={[1.02, 0.44, 0.07]} />
            <meshStandardMaterial color="#0a120d" roughness={0.7} />
          </mesh>
          <mesh>
            <planeGeometry args={[0.9, 0.34]} />
            <meshBasicMaterial map={exitTex} toneMapped={false} />
          </mesh>
          <pointLight color="#2ee06f" intensity={2.2} distance={3.2} position={[0, -0.2, 0.3]} />
        </group>
      ))}
    </group>
  );
}

// self-standing A-frame step ladder: rungs stay square to the rails
function StepLadder({ position, rotation }) {
  const L = 2.6;
  const A = 0.17;
  const lean = L * Math.sin(A);
  const top = L * Math.cos(A);
  const alu = <meshStandardMaterial color="#8a8f99" metalness={0.55} roughness={0.42} />;
  const Side = ({ z, tilt, steps }) => (
    <group position={[0, 0, z]} rotation={[tilt, 0, 0]}>
      {[-0.24, 0.24].map((x) => (
        <mesh key={x} castShadow position={[x, L / 2, 0]}>
          <boxGeometry args={[0.055, L, 0.05]} />
          {alu}
        </mesh>
      ))}
      {steps.map((y) => (
        <mesh key={y} castShadow position={[0, y, 0]}>
          <boxGeometry args={[0.49, 0.042, steps.length > 2 ? 0.12 : 0.05]} />
          {alu}
        </mesh>
      ))}
    </group>
  );
  return (
    <group position={position} rotation={rotation}>
      <Side z={-lean} tilt={A} steps={[0.44, 0.88, 1.32, 1.76, 2.2]} />
      <Side z={lean} tilt={-A} steps={[1.0, 2.0]} />
      {/* top platform */}
      <mesh castShadow position={[0, top - 0.03, 0]}>
        <boxGeometry args={[0.6, 0.05, 0.44]} />
        {alu}
      </mesh>
      {/* spreader braces */}
      {[-0.22, 0.22].map((x) => (
        <mesh key={x} position={[x, 1.45, 0]}>
          <boxGeometry args={[0.035, 0.035, 2 * 1.45 * Math.sin(A)]} />
          {alu}
        </mesh>
      ))}
      {/* rubber feet */}
      {[[-0.24, -lean], [0.24, -lean], [-0.24, lean], [0.24, lean]].map(([x, z], i) => (
        <mesh key={i} position={[x, 0.02, z]}>
          <boxGeometry args={[0.075, 0.04, 0.075]} />
          <meshStandardMaterial color="#14161a" roughness={0.95} />
        </mesh>
      ))}
    </group>
  );
}

export function SetDressing() {
  const chairTex = useMemo(() => textTexture({ text: "DAVIDE CAMPAGNA", w: 512, h: 96, bg: "#101216", color: "#f8fafc", font: "900 48px Arial" }), []);
  return (
    <group>
      <group position={[0.5, 0, 2.4]} rotation={[0, -0.5, 0]}>
        <mesh castShadow position={[0, 0.45, 0]}>
          <boxGeometry args={[0.55, 0.05, 0.45]} />
          <meshStandardMaterial color="#7a5330" roughness={0.78} />
        </mesh>
        <mesh castShadow position={[0, 0.95, -0.2]}>
          <boxGeometry args={[0.55, 0.3, 0.04]} />
          <meshStandardMaterial color="#23262d" roughness={0.8} />
        </mesh>
        <mesh position={[0, 0.95, -0.225]} rotation={[0, Math.PI, 0]}>
          <planeGeometry args={[0.52, 0.14]} />
          <meshBasicMaterial map={chairTex} toneMapped={false} />
        </mesh>
        {[-0.22, 0.22].map((bx) => (
          <mesh key={bx} castShadow position={[bx, 0.72, -0.2]}>
            <cylinderGeometry args={[0.02, 0.02, 0.55, 8]} />
            <meshStandardMaterial color="#6b4626" roughness={0.85} />
          </mesh>
        ))}
        {[[-0.25, -0.2], [0.25, -0.2], [-0.25, 0.2], [0.25, 0.2]].map(([x, z], i) => (
          <mesh key={i} castShadow position={[x, 0.22, z]}>
            <cylinderGeometry args={[0.023, 0.028, 0.45, 8]} />
            <meshStandardMaterial color="#6b4626" roughness={0.85} />
          </mesh>
        ))}
        {[-0.2, 0.2].map((z) => (
          <mesh key={`b${z}`} position={[0, 0.16, z]} rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[0.015, 0.015, 0.5, 6]} />
            <meshStandardMaterial color="#6b4626" roughness={0.85} />
          </mesh>
        ))}
      </group>
      {[[-6.5, 3.5], [-6, 4.4], [14, 5.2], [-14.5, 5.5]].map(([x, z], i) => (
        <mesh key={i} castShadow position={[x, 0.4, z]} rotation={[0, i * 0.4, 0]}>
          <boxGeometry args={[1.1, 0.8, 0.7]} />
          <meshStandardMaterial color="#1b1e24" metalness={0.5} roughness={0.45} />
        </mesh>
      ))}
      {[[-8, 1], [7.5, -3], [12.5, -4], [-13, 0.5]].map(([x, z], i) => (
        <group key={i} position={[x, 0, z]}>
          <mesh castShadow position={[0, 1.4, 0]}>
            <cylinderGeometry args={[0.025, 0.035, 2.8, 8]} />
            <meshStandardMaterial color="#2e323a" metalness={0.7} roughness={0.35} />
          </mesh>
          <mesh castShadow position={[0.4, 2.5, 0]} rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[0.02, 0.02, 1.1, 8]} />
            <meshStandardMaterial color="#2e323a" metalness={0.7} roughness={0.35} />
          </mesh>
          <mesh castShadow position={[0.95, 1.9, 0]}>
            <boxGeometry args={[0.05, 1.3, 0.9]} />
            <meshStandardMaterial color="#0a0b0d" roughness={0.95} side={THREE.DoubleSide} />
          </mesh>
        </group>
      ))}
      <StepLadder position={[8.5, 0, -6.8]} rotation={[0, 0.9, 0]} />
      {/* catenary cables from truss */}
      {[[-6, 6.8, -2, -4, 0, -2.5], [3, 6.8, -4.5, 5, 0.8, -0.6]].map(([x1, y1, z1, x2, y2, z2], i) => {
        const pts = [];
        for (let t = 0; t <= 20; t++) {
          const f = t / 20;
          const sag = Math.sin(f * Math.PI) * 1.6;
          pts.push(new THREE.Vector3(x1 + (x2 - x1) * f, y1 + (y2 - y1) * f - sag, z1 + (z2 - z1) * f));
        }
        const geo = new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 24, 0.02, 6);
        return (
          <mesh key={i} geometry={geo}>
            <meshStandardMaterial color="#101114" roughness={0.7} />
          </mesh>
        );
      })}
    </group>
  );
}

let concreteTex = null;
function getConcreteTexture() {
  if (concreteTex) return concreteTex;
  const c = document.createElement("canvas");
  c.width = c.height = 512;
  const g = c.getContext("2d");
  g.fillStyle = "#3a3a3e";
  g.fillRect(0, 0, 512, 512);
  // mottled cement patches
  for (let i = 0; i < 2600; i++) {
    const r = Math.random();
    g.fillStyle = r < 0.33 ? "rgba(26,26,30,0.35)" : r < 0.66 ? "rgba(84,84,90,0.28)" : "rgba(58,54,50,0.3)";
    const s = 6 + Math.random() * 46;
    g.beginPath();
    g.ellipse(Math.random() * 512, Math.random() * 512, s, s * (0.5 + Math.random()), Math.random() * 3, 0, 6.283);
    g.fill();
  }
  // fine aggregate speckle
  for (let i = 0; i < 26000; i++) {
    g.fillStyle = Math.random() < 0.5 ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.09)";
    g.fillRect(Math.random() * 512, Math.random() * 512, 1.5, 1.5);
  }
  // trowel streaks + old paint scuffs
  g.strokeStyle = "rgba(120,118,112,0.1)";
  g.lineWidth = 8;
  for (let i = 0; i < 26; i++) {
    g.beginPath();
    const y = Math.random() * 512;
    g.moveTo(0, y);
    g.bezierCurveTo(170, y + 30, 340, y - 30, 512, y + 10);
    g.stroke();
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(14, 5);
  concreteTex = t;
  return t;
}

export function Stage() {
  const floor = useMemo(getConcreteTexture, []);
  return (
    <group>
      {/* one continuous floor: stage + corridor + cinema */}
      <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]} position={[13, 0, -2]}>
        <planeGeometry args={[64, 22]} />
        <meshStandardMaterial map={floor} color="#6b696d" roughness={0.6} metalness={0.14} envMapIntensity={0.7} />
      </mesh>      {/* stage back wall */}
      <mesh position={[0, 4, -11]} receiveShadow>
        <planeGeometry args={[38, 8]} />
        <meshStandardMaterial color="#0c0e12" roughness={0.95} />
      </mesh>
      {/* stage left wall */}
      <mesh position={[-19, 4, -1]} rotation={[0, Math.PI / 2, 0]} receiveShadow>
        <planeGeometry args={[20, 8]} />
        <meshStandardMaterial color="#0b0d11" roughness={0.95} />
      </mesh>
      {/* stage right wall with doorway (z -6..-2) */}
      <mesh position={[19, 4, -8.5]} rotation={[0, -Math.PI / 2, 0]} receiveShadow>
        <planeGeometry args={[5, 8]} />
        <meshStandardMaterial color="#0b0d11" roughness={0.95} />
      </mesh>
      <mesh position={[19, 4, 3.5]} rotation={[0, -Math.PI / 2, 0]} receiveShadow>
        <planeGeometry args={[11, 8]} />
        <meshStandardMaterial color="#0b0d11" roughness={0.95} />
      </mesh>
      {/* stage front wall */}
      <mesh position={[0, 4, 9]} rotation={[0, Math.PI, 0]}>
        <planeGeometry args={[38, 8]} />
        <meshStandardMaterial color="#090a0d" roughness={1} />
      </mesh>
      {/* ceiling over stage */}
      <mesh position={[0, 7.4, -1]} rotation={[Math.PI / 2, 0, 0]}>
        <planeGeometry args={[38, 20]} />
        <meshStandardMaterial color="#08090c" roughness={1} />
      </mesh>
    </group>
  );
}

// --- extra set dressing scattered on the floor ---
const CABLE_COILS = [
  [-3.6, 2.9, 0.4], [5.6, 1.9, -0.8], [-7.4, -1.6, 1.2], [9.2, -1.4, 0.2],
  [13.6, 1.2, -0.6], [-12.6, 3.4, 0.9], [1.2, 6.4, 0.3], [-16.2, -2.2, -0.4],
];
const SANDBAGS = [
  [-8.2, 1.4], [-7.8, 1.0], [7.2, -3.4], [7.6, -3.0], [12.2, -4.4],
  [-13.4, 0.9], [-1.4, 5.6], [-1.0, 5.9], [15.8, 2.2], [4.4, 6.2],
];
const APPLE_BOXES = [
  [-5.4, 4.6, 0.3], [-4.9, 4.9, 1.1], [6.8, 5.4, -0.4], [11.4, 3.8, 0.7],
  [-11.2, -4.4, 0.2], [16.4, -1.2, 1.3],
];
const TAPE_ROLLS = [[-5.2, 4.2], [6.4, 5.0], [-9.6, 2.4], [10.6, 4.2], [0.8, 7.2]];
const BOTTLES = [[-5.0, 4.4], [6.9, 5.2], [3.2, 6.6], [-10.2, 2.1], [14.6, 3.6], [-15.4, 1.2]];
const CASES = [[-6.9, 6.2, 0.5], [8.9, 6.4, -0.3], [-15.6, 3.8, 1.2], [17.2, 0.8, 0.4]];

export function FloorProps() {
  const cableMat = useMemo(() => new THREE.MeshStandardMaterial({ color: "#1c1f24", roughness: 0.85 }), []);
  const cableAlt = useMemo(() => new THREE.MeshStandardMaterial({ color: "#7a3b1f", roughness: 0.85 }), []);
  const cableBlue = useMemo(() => new THREE.MeshStandardMaterial({ color: "#26506e", roughness: 0.85 }), []);
  const bagMat = useMemo(() => new THREE.MeshStandardMaterial({ color: "#7a6540", roughness: 1 }), []);
  const bagAlt = useMemo(() => new THREE.MeshStandardMaterial({ color: "#5d4c30", roughness: 1 }), []);
  const woodMat = useMemo(() => new THREE.MeshStandardMaterial({ color: "#8a6535", roughness: 0.88 }), []);
  const tapeMat = useMemo(() => new THREE.MeshStandardMaterial({ color: "#c8a02a", roughness: 0.7 }), []);
  const caseMats = useMemo(
    () => ["#1f3c5a", "#5a2320", "#2f4a30", "#4a3a1c"].map((c) => new THREE.MeshStandardMaterial({ color: c, metalness: 0.4, roughness: 0.5 })),
    []
  );
  const coilMats = [cableMat, cableAlt, cableBlue];

  return (
    <group>
      {CABLE_COILS.map(([x, z, r], i) => (
        <mesh key={`coil-${i}`} castShadow material={coilMats[i % 3]} position={[x, 0.05, z]} rotation={[Math.PI / 2, 0, i * 0.7]}>
          <torusGeometry args={[0.34 * r + 0.1, 0.046, 7, 22]} />
        </mesh>
      ))}
      {SANDBAGS.map(([x, z], i) => (
        <mesh key={`bag-${i}`} castShadow material={i % 2 ? bagMat : bagAlt} position={[x, 0.08, z]} rotation={[0, i * 0.9, 0]}>
          <boxGeometry args={[0.44, 0.16, 0.26]} />
        </mesh>
      ))}
      {APPLE_BOXES.map(([x, z, r], i) => (
        <group key={`apple-${i}`} position={[x, 0, z]} rotation={[0, r * 3, 0]}>
          <mesh castShadow material={woodMat} position={[0, 0.13, 0]}>
            <boxGeometry args={[0.5, 0.26, 0.32]} />
          </mesh>
          {i % 2 === 0 && (
            <mesh castShadow material={woodMat} position={[0.04, 0.39, 0.03]} rotation={[0, 0.3, 0]}>
              <boxGeometry args={[0.5, 0.26, 0.32]} />
            </mesh>
          )}
        </group>
      ))}
      {TAPE_ROLLS.map(([x, z], i) => (
        <mesh key={`tape-${i}`} castShadow material={tapeMat} position={[x, 0.035, z]} rotation={[Math.PI / 2, 0, i]}>
          <torusGeometry args={[0.055, 0.032, 6, 14]} />
        </mesh>
      ))}
      {BOTTLES.map(([x, z], i) => (
        <mesh key={`bottle-${i}`} castShadow position={[x, 0.11, z]}>
          <cylinderGeometry args={[0.035, 0.035, 0.22, 8]} />
          <meshStandardMaterial color="#9fb6c9" transparent opacity={0.55} roughness={0.25} />
        </mesh>
      ))}
      {CASES.map(([x, z, r], i) => (
        <group key={`case-${i}`} position={[x, 0, z]} rotation={[0, r * 2.4, 0]}>
          <mesh castShadow material={caseMats[i % caseMats.length]} position={[0, 0.17, 0]}>
            <boxGeometry args={[0.92, 0.34, 0.56]} />
          </mesh>
          {/* aluminium corners + latch so it reads as a flight case */}
          <mesh position={[0, 0.345, 0]}>
            <boxGeometry args={[0.94, 0.03, 0.58]} />
            <meshStandardMaterial color="#9aa0a8" metalness={0.8} roughness={0.35} />
          </mesh>
          <mesh position={[0, 0.2, 0.29]}>
            <boxGeometry args={[0.14, 0.09, 0.03]} />
            <meshStandardMaterial color="#b9bfc6" metalness={0.85} roughness={0.3} />
          </mesh>
        </group>
      ))}
      {/* coloured gaffer-tape marks on the floor */}
      {[
        [-2.2, -1.4, "#e0b422", 0.3], [8.6, -6.4, "#d23f2f", -0.5], [-1.4, -2.6, "#2f7fd2", 0.9],
        [-9.5, -1.2, "#3fae5a", 0.2], [6.2, -1.8, "#e0b422", -1.1], [-5.6, 1.6, "#d23f2f", 0.6],
        [9.4, 3.2, "#2f7fd2", -0.3], [-12.2, 2.2, "#e0b422", 1.2],
      ].map(([x, z, c, r], i) => (
        <group key={`mark-${i}`} position={[x, 0.022, z]} rotation={[-Math.PI / 2, 0, r]}>
          <mesh>
            <planeGeometry args={[0.42, 0.06]} />
            <meshBasicMaterial color={c} />
          </mesh>
          <mesh position={[0, -0.16, 0]}>
            <planeGeometry args={[0.06, 0.32]} />
            <meshBasicMaterial color={c} />
          </mesh>
        </group>
      ))}
      <mesh castShadow position={[-17.6, 1.4, -6.2]} rotation={[0.16, 0, 0.09]}>
        <cylinderGeometry args={[0.13, 0.13, 2.9, 10]} />
        <meshStandardMaterial color="#3a2a22" roughness={1} />
      </mesh>
      <mesh castShadow position={[-17.3, 1.3, -5.6]} rotation={[0.2, 0, 0.14]}>
        <cylinderGeometry args={[0.11, 0.11, 2.7, 10]} />
        <meshStandardMaterial color="#23303a" roughness={1} />
      </mesh>
      {/* film cans stack */}
      <group position={[8.2, 0, 6.6]}>
        {[0, 1, 2].map((i) => (
          <mesh key={i} castShadow position={[0, 0.04 + i * 0.075, 0]} rotation={[0, i * 0.4, 0]}>
            <cylinderGeometry args={[0.24, 0.24, 0.07, 18]} />
            <meshStandardMaterial color="#5c5f66" metalness={0.65} roughness={0.42} />
          </mesh>
        ))}
      </group>
      {/* fire extinguisher */}
      <group position={[-9.1, 0, 6.4]}>
        <mesh castShadow position={[0, 0.3, 0]}>
          <cylinderGeometry args={[0.1, 0.1, 0.6, 14]} />
          <meshStandardMaterial color="#8c1414" roughness={0.45} metalness={0.3} />
        </mesh>
        <mesh castShadow position={[0, 0.66, 0]}>
          <cylinderGeometry args={[0.03, 0.03, 0.12, 8]} />
          <meshStandardMaterial color="#1a1c20" roughness={0.6} />
        </mesh>
      </group>
      {/* broom leaning */}
      <group position={[16.8, 0, 7.4]} rotation={[0.14, 0.6, 0.1]}>
        <mesh castShadow position={[0, 0.72, 0]}>
          <cylinderGeometry args={[0.022, 0.022, 1.45, 8]} />
          <meshStandardMaterial color="#7a5c33" roughness={0.9} />
        </mesh>
        <mesh castShadow position={[0, 0.03, 0.03]}>
          <boxGeometry args={[0.36, 0.06, 0.1]} />
          <meshStandardMaterial color="#2b2118" roughness={1} />
        </mesh>
      </group>
      {/* coffee cups on an apple box */}
      {[[-5.32, 4.52], [-5.2, 4.68]].map(([x, z], i) => (
        <mesh key={`cup-${i}`} castShadow position={[x, 0.3, z]}>
          <cylinderGeometry args={[0.032, 0.026, 0.085, 10]} />
          <meshStandardMaterial color="#d9d2c4" roughness={0.8} />
        </mesh>
      ))}
    </group>
  );
}
